import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MailIcon, PhoneIcon, ArrowLeftIcon, ArrowRightIcon, XIcon, UserIcon, CameraIcon, UploadCloudIcon, ShieldCheckIcon, CheckCircle2Icon, ChevronDownIcon, CheckIcon, MessageCircleIcon } from 'lucide-react';
import { Logo } from './Logo';
import { ConsentCheckbox } from './legal/ConsentCheckbox';
type DialCode = {
  iso: string;
  dial: string;
  name: string;
};
// UAE first, then other GCC markets.
const DIAL_CODES: DialCode[] = [{
  iso: 'ae',
  dial: '+971',
  name: 'United Arab Emirates'
}, {
  iso: 'sa',
  dial: '+966',
  name: 'Saudi Arabia'
}, {
  iso: 'qa',
  dial: '+974',
  name: 'Qatar'
}, {
  iso: 'kw',
  dial: '+965',
  name: 'Kuwait'
}, {
  iso: 'bh',
  dial: '+973',
  name: 'Bahrain'
}, {
  iso: 'om',
  dial: '+968',
  name: 'Oman'
}];
function CountryFlag({
  iso
}: {
  iso: string;
}) {
  return <img src={`https://flagcdn.com/w40/${iso}.png`} srcSet={`https://flagcdn.com/w80/${iso}.png 2x`} width={20} height={15} alt="" aria-hidden="true" className="rounded-[3px] object-cover shadow-sm ring-1 ring-black/5" data-id="element-2665" data-mp="132n92tsbwcq9" />;
}
// Phone input with a separated, selectable country dial-code prefix.
function PhoneField({
  value,
  onChange,
  dialCode,
  onDialCodeChange
}: {
  value: string;
  onChange: (v: string) => void;
  dialCode: DialCode;
  onDialCodeChange: (d: DialCode) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return <div className="flex items-stretch gap-2" data-id="element-2666" data-mp="3sknp0czidqd3">
      {/* Dial-code prefix */}
      <div className="relative" ref={ref} data-id="element-2667" data-mp="3ivgwvx3mlkcl">
        <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} className={`h-full flex items-center gap-2 px-3 border rounded-xl transition-colors ${open ? 'border-primary-indigo bg-primary-indigo/5' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-id="element-2668" data-mp="3ixteua9dvupn">
          <CountryFlag iso={dialCode.iso} data-id="element-2669" data-mp="5hvfhyqo9cf7" />
          <span className="text-sm font-semibold text-slate-700" data-id="element-2670" data-mp="5ix12x5bdhb6">
            {dialCode.dial}
          </span>
          <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180 text-primary-indigo' : ''}`} data-id="element-2671" data-mp="3rwetvvfncrlo" />
        </button>
        <AnimatePresence data-id="element-2672" data-mp="oasf1qma2us">
          {open && <motion.div role="listbox" initial={{
          opacity: 0,
          y: -6,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -6,
          scale: 0.97
        }} transition={{
          duration: 0.15
        }} className="absolute left-0 mt-2 w-64 bg-white border border-slate-100 rounded-2xl shadow-[0_16px_48px_-8px_rgb(0,0,0,0.18)] overflow-hidden z-50 p-1.5 space-y-0.5" data-id="element-2673" data-mp="of6d7ewnnxsj">
              {DIAL_CODES.map(d => {
            const active = d.iso === dialCode.iso;
            return <button key={d.iso} type="button" role="option" aria-selected={active} onClick={() => {
              onDialCodeChange(d);
              setOpen(false);
            }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${active ? 'bg-primary-indigo/[0.06]' : 'hover:bg-slate-50'}`} data-id="element-2674" data-mp="23yw7u5c9gslm">
                    <CountryFlag iso={d.iso} data-id="element-2675" data-mp="ehto7nx47tkl" />
                    <span className="flex-1 min-w-0" data-id="element-2676" data-mp="eko73b0hd6bw">
                      <span className={`block text-sm truncate ${active ? 'font-semibold text-primary-indigo' : 'font-medium text-slate-800'}`} data-id="element-2677" data-mp="jgn0xe4kvgjp">
                        {d.name}
                      </span>
                    </span>
                    <span className={`text-sm font-semibold ${active ? 'text-primary-indigo' : 'text-slate-400'}`} data-id="element-2678" data-mp="xgxrsh5w0v4v">
                      {d.dial}
                    </span>
                    {active && <CheckIcon className="w-4 h-4 text-primary-indigo shrink-0" data-id="element-2679" data-mp="176hb1nxiir09" />}
                  </button>;
          })}
            </motion.div>}
        </AnimatePresence>
      </div>
      {/* Local number */}
      <input type="tel" value={value} onChange={e => onChange(e.target.value)} placeholder="50 123 4567" className="flex-1 min-w-0 px-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-2680" data-mp="31nr412fieiqj" />
    </div>;
}
type Step = 'method' | 'email' | 'phone' | 'otp' | 'profile' | 'verify';
interface LoginProps {
  onLogin: () => void;
  onClose?: () => void;
}
function GoogleMark() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true" data-id="element-2681" data-mp="3ff5lv5xokdw0">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" data-id="element-2682" data-mp="3fh8d1takkd2b" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" data-id="element-2683" data-mp="35ojg03frn1qi" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" data-id="element-2684" data-mp="27ayxlsv45wu" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38z" data-id="element-2685" data-mp="1wgzom64q7w16" />
    </svg>;
}
function AppleMark() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" data-id="element-2686" data-mp="1rphkj97epfbz">
      <path d="M16.36 12.78c-.02-2.05 1.67-3.03 1.75-3.08-.95-1.39-2.43-1.58-2.96-1.6-1.26-.13-2.46.74-3.1.74-.64 0-1.62-.72-2.67-.7-1.37.02-2.64.8-3.35 2.03-1.43 2.48-.37 6.15 1.02 8.16.68.98 1.49 2.08 2.55 2.04 1.02-.04 1.41-.66 2.65-.66 1.23 0 1.58.66 2.66.64 1.1-.02 1.79-1 2.46-1.99.78-1.14 1.1-2.24 1.12-2.3-.02-.01-2.15-.83-2.18-3.27zM14.3 6.58c.56-.68.94-1.62.84-2.58-.81.03-1.79.54-2.37 1.22-.52.6-.98 1.56-.86 2.48.9.07 1.83-.46 2.39-1.12z" data-id="element-2687" data-mp="1dp6si2ud1up8" />
    </svg>;
}
export function Login({
  onLogin,
  onClose
}: LoginProps) {
  const [step, setStep] = useState<Step>('method');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dialCode, setDialCode] = useState<DialCode>(DIAL_CODES[0]);
  const [fullName, setFullName] = useState('');
  const [idStatus, setIdStatus] = useState<'idle' | 'uploaded'>('idle');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Explicit agreement is required before any sign-up method can continue.
  const [agreed, setAgreed] = useState(false);
  const [nudge, setNudge] = useState(false);
  const withConsent = (action: () => void) => () => {
    if (!agreed) {
      setNudge(true);
      return;
    }
    action();
  };
  const goToOtp = () => setStep('otp');
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };
  const otpComplete = otp.every(d => d !== '');
  const stepVariants = {
    enter: {
      opacity: 0,
      x: 24
    },
    center: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -24
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-id="element-2688" data-mp="39w245j39vhil">
      <div className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" onClick={onClose} data-id="element-2689" data-mp="2m3v8km5dfek8" />
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="relative w-full max-w-md bg-white rounded-3xl shadow-[0_20px_60px_rgb(0,0,0,0.18)] border border-slate-100 p-8 z-10" data-id="element-2690" data-mp="2q71d9pcu4xol">
        {onClose && <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-50 rounded-full transition-colors" data-id="element-2691" data-mp="1pxa3x3ywjdqc">
            <XIcon className="w-5 h-5" data-id="element-2692" data-mp="1h356g1p9j41u" />
          </button>}
        <div className="flex justify-center mb-6" data-id="element-2693" data-mp="1c5viyrlfp2gn">
          <Logo size="md" data-id="element-2694" data-mp="17726n2hvvhi2" />
        </div>

        <AnimatePresence mode="wait" data-id="element-2695" data-mp="1783jncigme2f">
          {/* METHOD SELECTION */}
          {step === 'method' && <motion.div key="method" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2696" data-mp="126w7z2d74uqh">
              <div className="text-center mb-7" data-id="element-2697" data-mp="t93qpsnwzd0g">
                <h1 className="font-display font-bold text-2xl text-dark-navy mb-2" data-id="element-2698" data-mp="2skdaglvzyi5h">
                  Welcome to Talab
                </h1>
                <p className="text-slate-500 text-sm" data-id="element-2699" data-mp="2nmv85echj16g">
                  Sign in to let your AI agent find anything for you.
                </p>
              </div>

              <div className="space-y-3" data-id="element-2700" data-mp="czdwq8p808ah">
                <button onClick={withConsent(() => setStep('phone'))} className="w-full flex items-center justify-center gap-3 py-3.5 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-2701" data-mp="cycjbxkqf9pl">
                  <GoogleMark data-id="element-2702" data-mp="3ug5l5gx42bfe" />
                  Continue with Google
                </button>
                <button onClick={withConsent(() => setStep('phone'))} className="w-full flex items-center justify-center gap-3 py-3.5 bg-dark-navy text-white rounded-xl font-medium hover:opacity-90 transition-opacity" data-id="element-2703" data-mp="3qf244r3bu9cl">
                  <AppleMark data-id="element-2704" data-mp="3boconciu282x" />
                  Continue with Apple
                </button>

                <div className="flex items-center gap-3 py-2" data-id="element-2705" data-mp="36q1j3dzkmjqi">
                  <div className="flex-1 h-px bg-slate-100" data-id="element-2706" data-mp="36nozkwe601bc" />
                  <span className="text-xs text-slate-400 font-medium" data-id="element-2707" data-mp="1m6o6b5pdcd57">OR</span>
                  <div className="flex-1 h-px bg-slate-100" data-id="element-2708" data-mp="1qyz4o44gnqvn" />
                </div>

                <button onClick={withConsent(() => setStep('phone'))} className="w-full flex items-center justify-center gap-3 py-3.5 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-2709" data-mp="1d7avo9pidiiw">
                  <PhoneIcon className="w-5 h-5 text-primary-indigo" data-id="element-2710" data-mp="29xbhbzukzivz" />
                  Continue with Phone
                </button>
                {/* Email is wired as instant access so the prototype can be
                    explored without stepping through OTP and verification. */}
                <button onClick={withConsent(onLogin)} className="w-full flex items-center justify-center gap-3 py-3.5 border border-slate-200 rounded-xl font-medium text-slate-700 hover:border-primary-indigo/40 hover:bg-slate-50 transition-colors" data-id="element-2711" data-mp="20ygbiovg8at7">
                  <MailIcon className="w-5 h-5 text-primary-indigo" data-id="element-2712" data-mp="5h6o38qiq79w" />
                  Continue with Email
                  <span className="px-2 py-0.5 rounded-full bg-primary-indigo/[0.07] text-[10px] font-bold uppercase tracking-wider text-primary-indigo" data-id="element-2713" data-mp="3ssfzfut97ioe">
                    Instant
                  </span>
                </button>
              </div>

              <div className="mt-6 pt-5 border-t border-slate-100" data-id="element-2714" data-mp="3iv1no8i9zi2f">
                <ConsentCheckbox checked={agreed} onChange={v => {
              setAgreed(v);
              if (v) setNudge(false);
            }} docs={['terms', 'privacy']} lead="I agree to Talab’s" attention={nudge} data-id="element-2715" data-mp="3dunjx2npiua7" />
                {nudge && !agreed && <p role="alert" className="text-[12px] text-error mt-2 ml-[30px]" data-id="element-2716" data-mp="133dqqd39hpjm">
                    Please agree to continue.
                  </p>}
              </div>
            </motion.div>}

          {/* EMAIL STEP */}
          {step === 'email' && <motion.div key="email" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2717" data-mp="1kcg7sw8shj7n">
              <button onClick={() => setStep('method')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-indigo mb-5 transition-colors" data-id="element-2718" data-mp="1pdcflkw9hpic">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2719" data-mp="11m6vuun7ad2r" />
                Back
              </button>
              <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-2720" data-mp="15nad9lkwch63">
                Sign in with email
              </h1>
              <p className="text-slate-500 text-sm mb-6" data-id="element-2721" data-mp="2d02fafl517zs">
                We'll also verify your phone number to secure your account.
              </p>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2722" data-mp="2fljvzcl6dgjl">
                Email address
              </label>
              <div className="relative mb-4" data-id="element-2723" data-mp="2an8nb56m9c2y">
                <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-2724" data-mp="2aojqp3w5ghtj" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-2725" data-mp="2phd50lzoqrwm" />
              </div>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2726" data-mp="389fmu0o2ce8g">
                Phone number <span className="text-error" data-id="element-2727" data-mp="33bwax7eu8djo">*</span>
              </label>
              <div className="mb-6" data-id="element-2728" data-mp="3hbx9gg7mr8vz">
                <PhoneField value={phone} onChange={setPhone} dialCode={dialCode} onDialCodeChange={setDialCode} data-id="element-2729" data-mp="3h8t2jb2s5z1m" />
                <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1" data-id="element-2730" data-mp="3vvrpasqp05a0">
                  <MessageCircleIcon className="w-3.5 h-3.5 text-[#25D366]" data-id="element-2731" data-mp="4qi4dfhk2h79" />
                  Required — we'll send a one-time code via WhatsApp to verify
                  it.
                </p>
              </div>

              <button onClick={goToOtp} disabled={!email.includes('@') || phone.replace(/\D/g, '').length < 7} className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2732" data-mp="3rxtwzh1ml32e">
                Send verification code
                <ArrowRightIcon className="w-4 h-4" data-id="element-2733" data-mp="2u8qxysson46c" />
              </button>
            </motion.div>}

          {/* PHONE STEP */}
          {step === 'phone' && <motion.div key="phone" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2734" data-mp="14rd717ztotsz">
              <button onClick={() => setStep('method')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-indigo mb-5 transition-colors" data-id="element-2735" data-mp="vy9wb7gkav21">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2736" data-mp="1noy0ksdj8gjx" />
                Back
              </button>
              <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-2737" data-mp="1jpfevudw01g1">
                Verify your phone
              </h1>
              <p className="text-slate-500 text-sm mb-6 flex items-center gap-1.5" data-id="element-2738" data-mp="1engcampz18k5">
                <MessageCircleIcon className="w-4 h-4 text-[#25D366] shrink-0" data-id="element-2739" data-mp="19poifn5rncdb" />
                Enter your phone number and we'll send a one-time code via
                WhatsApp.
              </p>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2740" data-mp="3ueueyicwq7rg">
                Phone number <span className="text-error" data-id="element-2741" data-mp="3ke1u6skitl4h">*</span>
              </label>
              <div className="mb-6" data-id="element-2742" data-mp="3pasb1i45gkqy">
                <PhoneField value={phone} onChange={setPhone} dialCode={dialCode} onDialCodeChange={setDialCode} data-id="element-2743" data-mp="3pbux9cqj1z0j" />
              </div>

              <button onClick={goToOtp} disabled={phone.replace(/\D/g, '').length < 7} className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2744" data-mp="2xtmwyugi2cj">
                Send verification code
                <ArrowRightIcon className="w-4 h-4" data-id="element-2745" data-mp="2in4tpttk6e77" />
              </button>
            </motion.div>}

          {/* OTP STEP */}
          {step === 'otp' && <motion.div key="otp" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2746" data-mp="2nmhpx56bkxmg">
              <button onClick={() => setStep(email ? 'email' : 'phone')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-indigo mb-5 transition-colors" data-id="element-2747" data-mp="oerka8sbtoav">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2748" data-mp="y7gk26eildqd" />
                Back
              </button>
              <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-2749" data-mp="ahcqkrv1iud3">
                Enter verification code
              </h1>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-3 rounded-full bg-[#25D366]/10 text-[#1da851] text-xs font-medium" data-id="element-2750" data-mp="jgg9nvu7j9rl">
                <MessageCircleIcon className="w-3.5 h-3.5" data-id="element-2751" data-mp="fgpc99a6dve3" />
                Sent via WhatsApp
              </div>
              <p className="text-slate-500 text-sm mb-6" data-id="element-2752" data-mp="1ux7ksndefx4m">
                We sent a 6-digit code on WhatsApp to{' '}
                <span className="font-medium text-slate-700" data-id="element-2753" data-mp="1ydk8gh12jjli">
                  {phone ? `${dialCode.dial} ${phone}` : 'your phone'}
                </span>
                .
              </p>

              <div className="flex justify-between gap-2 mb-6" data-id="element-2754" data-mp="1ofcv085znjo9">
                {otp.map((digit, i) => <input key={i} ref={el => otpRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} aria-label={`Digit ${i + 1}`} className="w-12 h-14 text-center text-xl font-bold border border-slate-200 rounded-xl outline-none focus:border-primary-indigo focus:ring-2 focus:ring-primary-indigo/20 transition-all" data-id="element-2755" data-mp="1tdxzwkw6qrj1" data-mpi={i} />)}
              </div>

              <button onClick={() => setStep('profile')} disabled={!otpComplete} className="w-full py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2756" data-mp="108jfly9hb1ib">
                Verify & continue
              </button>

              <p className="text-center text-sm text-slate-500 mt-5" data-id="element-2757" data-mp="vg02t3im8aek">
                Didn't get the code?{' '}
                <button className="font-medium text-primary-indigo hover:underline" data-id="element-2758" data-mp="ljx1fzvl8fso">
                  Resend
                </button>
              </p>
            </motion.div>}

          {/* PROFILE COMPLETION STEP */}
          {step === 'profile' && <motion.div key="profile" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2759" data-mp="cjqoqg7prjuk">
              <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-2760" data-mp="26wqe311wbsf1">
                Confirm your details
              </h1>
              <p className="text-slate-500 text-sm mb-6" data-id="element-2761" data-mp="3mgpm8fld11wx">
                Let's make sure we have your name and email right.
              </p>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2762" data-mp="50r8gjfju8ff">
                Full name <span className="text-error" data-id="element-2763" data-mp="87tpj54l791">*</span>
              </label>
              <div className="relative mb-4" data-id="element-2764" data-mp="e04jhgjbn529">
                <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-2765" data-mp="dyt5yp02qixy" />
                <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="e.g. Ahmed Al Mansoori" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-2766" data-mp="91b2he4tsvxc" />
              </div>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2767" data-mp="2ksy9osraiqts">
                Email address <span className="text-error" data-id="element-2768" data-mp="3ah2dnz05ndf6">*</span>
              </label>
              <div className="relative mb-6" data-id="element-2769" data-mp="3ehw4b04zf6br">
                <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-2770" data-mp="3einvlyrjqhw8" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-2771" data-mp="3of0owvj6itqg" />
              </div>

              <button onClick={() => setStep('verify')} disabled={fullName.trim().length < 2 || !email.includes('@')} className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2772" data-mp="6346nbxwrlmz">
                Continue
                <ArrowRightIcon className="w-4 h-4" data-id="element-2773" data-mp="20h9eohe802zw" />
              </button>
            </motion.div>}

          {/* ID VERIFICATION STEP */}
          {step === 'verify' && <motion.div key="verify" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2774" data-mp="1vjzqt66hbze6">
              <div className="flex flex-col items-center text-center mb-6" data-id="element-2775" data-mp="2o8wvu7lojzoz">
                <div className="w-14 h-14 rounded-2xl bg-primary-indigo/10 flex items-center justify-center mb-4" data-id="element-2776" data-mp="2j897jtou5wyg">
                  <ShieldCheckIcon className="w-7 h-7 text-primary-indigo" data-id="element-2777" data-mp="2eexyrn0gr63w" />
                </div>
                <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-2778" data-mp="29fv50ypxomb8">
                  Verify your identity
                </h1>
                <p className="text-slate-500 text-sm" data-id="element-2779" data-mp="3263d1xqr9qc3">
                  Scan or upload your ID. We rely on a verified ID to keep Talab
                  safe and unlock full access.
                </p>
              </div>

              {idStatus === 'uploaded' ? <div className="flex items-center gap-3 p-4 bg-success/5 border border-success/20 rounded-xl mb-6" data-id="element-2780" data-mp="2rlc1mb1snkh8">
                  <CheckCircle2Icon className="w-6 h-6 text-success shrink-0" data-id="element-2781" data-mp="2hp8yp2z4bht8" />
                  <div className="text-left" data-id="element-2782" data-mp="2hrbz8gxwctc7">
                    <p className="font-medium text-slate-900 text-sm" data-id="element-2783" data-mp="35g4ndr8a1ww9">
                      ID submitted
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2784" data-mp="3adek7rxzxau4">
                      We'll review it shortly — you can continue in the
                      meantime.
                    </p>
                  </div>
                </div> : <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-2785" data-mp="3odfjx42iyc80">
                  <button onClick={() => setIdStatus('uploaded')} className="flex flex-col items-center justify-center p-5 bg-slate-50 border border-slate-200 border-dashed rounded-xl hover:border-primary-indigo hover:bg-primary-indigo/5 transition-all group" data-id="element-2786" data-mp="3obutnqn8tffy">
                    <CameraIcon className="w-7 h-7 text-slate-400 group-hover:text-primary-indigo mb-2 transition-colors" data-id="element-2787" data-mp="1rf8kmlhkkgw4" />
                    <span className="font-medium text-sm text-slate-700 group-hover:text-primary-indigo transition-colors" data-id="element-2788" data-mp="1mg5f6zxog8nm">
                      Scan ID
                    </span>
                  </button>
                  <button onClick={() => setIdStatus('uploaded')} className="flex flex-col items-center justify-center p-5 bg-slate-50 border border-slate-200 border-dashed rounded-xl hover:border-primary-indigo hover:bg-primary-indigo/5 transition-all group" data-id="element-2789" data-mp="tsrqfp44ujel">
                    <UploadCloudIcon className="w-7 h-7 text-slate-400 group-hover:text-primary-indigo mb-2 transition-colors" data-id="element-2790" data-mp="2j53aqkd94alu" />
                    <span className="font-medium text-sm text-slate-700 group-hover:text-primary-indigo transition-colors" data-id="element-2791" data-mp="2f7f9kzkytlli">
                      Upload ID
                    </span>
                  </button>
                </div>}

              <button onClick={onLogin} className="w-full py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors" data-id="element-2792" data-mp="1lte9jdwfqjy0">
                {idStatus === 'uploaded' ? 'Finish & continue' : 'Continue'}
              </button>
              {idStatus !== 'uploaded' && <button onClick={onLogin} className="w-full mt-2 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors" data-id="element-2793" data-mp="124dmjv94ck9a">
                  Verify later
                </button>}
            </motion.div>}
        </AnimatePresence>
      </motion.div>
    </motion.div>;
}